import { useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "../../lib/cn.js";

/**
 * Right-anchored slide-over drawer. Used for the form-submission detail
 * inbox in Phase 10 and any long-form view where keeping the list visible
 * matters.
 */
export function Drawer({ open, onClose, title, description, children, footer, width = "md" }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        className={cn(
          "absolute right-0 top-0 flex h-full flex-col border-l border-border bg-bg-elevated shadow-card-hover",
          {
            "w-full max-w-md": width === "sm",
            "w-full max-w-lg": width === "md",
            "w-full max-w-2xl": width === "lg",
            "w-full max-w-3xl": width === "xl",
          }
        )}
      >
        <div className="flex items-start justify-between border-b border-border-subtle px-6 py-4">
          <div>
            {title && <h2 className="text-lg font-display text-text-primary">{title}</h2>}
            {description && <p className="mt-1 text-sm text-text-secondary">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-border-subtle px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
